import React, { useCallback } from 'react';
import Button from '@atlaskit/button';

import { ModalHeader, ModalBody, ModalFooter, ModalTitle } from '@atlaskit/modal-dialog';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	MODAL,
	ContextualAnalyticsData,
} from '@atlassian/jira-product-analytics-bridge';
import { ModalDialog } from '@atlassian/jira-software-modal-dialog/src/ui/modal-dialog/index.tsx';
import {
	MoveCardsActiveSprintType,
	type MoveCardsActiveSprint,
} from '../../../model/modals/types.tsx';
import type { Card } from '../../../model/types.tsx';
import { cardsMoveRequest } from '../../../state/actions/cards/move/index.tsx';
import { closeModal } from '../../../state/actions/modals/index.tsx';
import { useBacklogDispatch, useBacklogSelector } from '../../../state/index.tsx';
import { getCards } from '../../../state/selectors/cards/index.tsx';
import { getOpenModalData } from '../../../state/selectors/modals/index.tsx';
import messages from './messages.tsx';

// Everything might be nullable due to the early return.
export type StateProps = Partial<
	MoveCardsActiveSprint['payload'] & {
		cards: Card[];
	}
>;

export function MoveCardsActiveSprintModal() {
	const data = useBacklogSelector((state) => {
		const cards = getCards(state);
		const modalData = getOpenModalData(state);

		if (!modalData || modalData.type !== MoveCardsActiveSprintType) {
			return {};
		}

		return {
			confirmActionPayload: modalData.payload.confirmActionPayload,
			originCardListName: modalData.payload.originCardListName,
			destinationCardListName: modalData.payload.destinationCardListName,
			analyticsEvent: modalData.payload.analyticsEvent,
			cards: modalData.payload.cardIds
				// Make sure the card exists
				.filter((id) => cards[String(id)])
				.map((id) => cards[String(id)]),
		};
	});
	const dispatch = useBacklogDispatch();
	const { formatMessage } = useIntl();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	/*
      Should only need to create this once since the modal is closed
      by either confirm or cancel
    */
	const analyticsEvent = createAnalyticsEvent({
		actionSubject: 'button',
		action: 'clicked',
	});

	const onClose = useCallback(() => {
		fireUIAnalytics(analyticsEvent, 'cancelButton');
		dispatch(closeModal());
	}, [analyticsEvent, dispatch]);

	const onMove = useCallback(() => {
		fireUIAnalytics(analyticsEvent, 'confirmButton');
		// Wrapping in an if as confirmActionPayload has the potential to be undefined
		if (data.confirmActionPayload) {
			dispatch(cardsMoveRequest(data.confirmActionPayload, data.analyticsEvent));
		}
		onClose();
	}, [analyticsEvent, onClose, data, dispatch]);

	const renderMovedIssues = useCallback(() => {
		const cards = data.cards || [];
		if (cards.length <= 0) {
			return null;
		}
		if (cards.length === 1) {
			return (
				<FormattedMessage
					{...messages.movedOneCard}
					values={{
						cardName: <b>{cards[0].key}</b>,
						originCardListName: <b>{data.originCardListName}</b>,
						destinationCardListName: <b>{data.destinationCardListName}</b>,
					}}
				/>
			);
		}

		if (cards.length === 2) {
			return (
				<FormattedMessage
					{...messages.movedTwoCards}
					values={{
						cardName: <b>{cards[0].key}</b>,
						anotherCardName: <b>{cards[1].key}</b>,
						originCardListName: <b>{data.originCardListName}</b>,
						destinationCardListName: <b>{data.destinationCardListName}</b>,
					}}
				/>
			);
		}

		return (
			<FormattedMessage
				{...(expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.movedMoreCardsIssueTermRefresh
					: messages.movedMoreCards)}
				values={{
					cardName: <b>{cards[0].key}</b>,
					anotherCardName: <b>{cards[1].key}</b>,
					originCardListName: <b>{data.originCardListName}</b>,
					destinationCardListName: <b>{data.destinationCardListName}</b>,
					moreCardsCountText: <b>{cards.length - 2}</b>,
					moreCardsCount: cards.length - 2,
				}}
			/>
		);
	}, [data]);

	const buttonCancel = (
		<Button appearance="subtle" onClick={onClose} key="buttonCancel">
			{formatMessage(messages.cancel)}
		</Button>
	);
	const buttonConfirm = (
		<Button appearance="primary" onClick={onMove} key="buttonConfirm">
			{formatMessage(messages.confirm)}
		</Button>
	);

	return (
		<ShortcutScope>
			<ModalDialog width={400} onClose={onClose}>
				<ModalHeader>
					<ModalTitle appearance="warning">
						{formatMessage(
							expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
								? messages.titleIssueTermRefresh
								: messages.title,
						)}
					</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<p>{formatMessage(messages.changedScope)}</p>
					<p data-testid="software-backlog.modals.move-cards-active-sprint-modal.content">
						{renderMovedIssues()}
					</p>
					<ViewTracker />
				</ModalBody>
				<ModalFooter>{[buttonCancel, buttonConfirm]}</ModalFooter>
			</ModalDialog>
		</ShortcutScope>
	);
}

export default function MoveCardsActiveSprintModalWithAnalytics() {
	return (
		<ContextualAnalyticsData sourceType={MODAL} sourceName="moveCardsActiveSprint">
			<MoveCardsActiveSprintModal />
		</ContextualAnalyticsData>
	);
}
